// Shared, purely presentational record-page chrome. These primitives capture
// the repeated non-domain layout that every shipped operational record page
// already uses: the app frame + Header, the loading state, the not-found state,
// the centered content wrapper, the back link, and the record title.
//
// They know nothing about entity types, routes, Supabase, auth, Comments,
// Activity, sequence navigation, or mutations. Pages keep their own data
// loading and branching and compose these pieces. Sequence navigation
// (RecordSequenceNav), Comments/Activity (RecordCollaborationSection), and
// inline notices (InlineNotice) remain page-composed and are intentionally not
// part of this shell.
//
// Page frame (Design Constitution N07, N09, Q41): the back link is one gray
// '‹ Herds' above the title; the title is the Title Case H1 at 28–34px / 800
// (index.html .wcf-page-title); loaded record pages default to the 960px
// record width with the frame's side padding (index.html .wcf-record-body).
// A page that passes its own width or title size keeps it until its area wave.

import type {ComponentType, CSSProperties, HTMLAttributes, ReactNode} from 'react';

// The frame renders `<Header />` with no props, so a caller's Header must be
// mountable on its own. It is optional: the two equipment entry pages render
// these states without one.
type WithHeader = {Header?: ComponentType};

// Every state forwards its unmatched props onto the frame's root <div>, which is
// how pages attach their own readiness data-attributes.
type DivPassthrough = Omit<HTMLAttributes<HTMLDivElement>, 'style'>;

// A label that already carries an arrow ('← Processing') renders it once.
export function backLabelText(label: ReactNode): ReactNode {
  return typeof label === 'string' ? label.replace(/^\s*[←‹]\s*/, '') : label;
}

function BackLinkButton({label, onBack}: {label?: ReactNode; onBack?: () => void}) {
  return (
    <button type="button" className="wcf-back-link" data-record-back="1" onClick={onBack}>
      <span aria-hidden="true">‹</span> {backLabelText(label)}
    </button>
  );
}

// Outer app frame: full-height neutral background plus the optional Header.
// Used by every record-page state (loading, not-found, loaded). Extra props
// (e.g. a state-specific data attribute like data-access-denied="1") are spread
// onto the frame's root div.
type RecordPageFrameProps = WithHeader & DivPassthrough & {children?: ReactNode};

export function RecordPageFrame({Header, children, ...rest}: RecordPageFrameProps) {
  return (
    <div {...rest} style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
      {Header && <Header />}
      {children}
    </div>
  );
}

// Centered loading state.
type RecordPageLoadingProps = WithHeader & DivPassthrough & {label?: ReactNode};

export function RecordPageLoading({Header, label = 'Loading…', ...rest}: RecordPageLoadingProps) {
  return (
    <RecordPageFrame Header={Header} {...rest}>
      <div style={{padding: 24, textAlign: 'center', color: 'var(--ink-muted)', fontSize: 14}}>{label}</div>
    </RecordPageFrame>
  );
}

// Not-found state: the back link above an explanatory message. Extra props
// (e.g. a state-specific frame data attribute) are forwarded to the frame's
// root div.
type RecordPageNotFoundProps = WithHeader &
  DivPassthrough & {
    backLabel?: ReactNode;
    onBack?: () => void;
    message?: ReactNode;
  };

export function RecordPageNotFound({Header, backLabel, onBack, message, ...rest}: RecordPageNotFoundProps) {
  return (
    <RecordPageFrame Header={Header} {...rest}>
      <div className="wcf-record-body" style={{maxWidth: 960, margin: '0 auto'}}>
        <BackLinkButton label={backLabel} onBack={onBack} />
        <div style={{marginTop: 16, color: 'var(--text-2)', fontSize: 14}}>{message}</div>
      </div>
    </RecordPageFrame>
  );
}

// Centered content wrapper for the loaded record. `maxWidth` defaults to the
// 960px record width (Q41); a page that passes its own keeps it. Side padding
// comes from the frame (index.html .wcf-record-body: 20/24px desktop, 16px
// phone). An optional `style` merges last. Any other props (e.g. a
// page-specific data attribute) are spread onto the div.
type RecordPageBodyProps = DivPassthrough & {
  children?: ReactNode;
  maxWidth?: CSSProperties['maxWidth'];
  style?: CSSProperties;
};

export function RecordPageBody({children, maxWidth = 960, style, ...rest}: RecordPageBodyProps) {
  return (
    <div {...rest} className="wcf-record-body" style={{maxWidth, margin: '0 auto', ...style}}>
      {children}
    </div>
  );
}

// Back link shown at the top of a loaded record page (N07).
type RecordBackLinkProps = {label?: ReactNode; onBack?: () => void};

export function RecordBackLink({label, onBack}: RecordBackLinkProps) {
  return (
    <div style={{marginBottom: 4}}>
      <BackLinkButton label={label} onBack={onBack} />
    </div>
  );
}

// Record page title (N09): the page-title H1, 28–34px / 800 from index.html.
// `fontSize` stays configurable for the denser title rows that pass one until
// their area wave; `margin` defaults to '0 0 12px'. `style` merges last.
type RecordTitleProps = {
  children?: ReactNode;
  fontSize?: CSSProperties['fontSize'];
  margin?: CSSProperties['margin'];
  style?: CSSProperties;
};

export function RecordTitle({children, fontSize, margin = '0 0 12px', style}: RecordTitleProps) {
  return (
    <h1
      data-record-title="1"
      className="wcf-page-title"
      style={{...(fontSize != null ? {fontSize} : null), margin, ...style}}
    >
      {children}
    </h1>
  );
}
